import { OrderSendForm } from '@/types/order';
import { useFormContext } from 'react-hook-form';
import { receivingType } from '@/types/checkout';
import { FormattedMessage } from 'react-intl';
import PageViewEvent from '../Event/PageViewEvent';
import { trackEvent } from '../Event/_lib/eventTrack';
const getLocationDefault = (id: string) => (id === 'pick_up_store' ? 'Pick-up Store' : 'Address');

interface IProps {
  onClose?: () => void;
  onContinueClick?: () => void;
  total: string;
  isSubmit: boolean;
  receivingMethod: receivingType;
}

const CheckModal = ({ onClose, onContinueClick, total, isSubmit, receivingMethod }: IProps) => {
  const { getValues } = useFormContext<OrderSendForm>();
  const domesticAddress = [getValues('shipping_info_address_1'), getValues('shipping_info_address_2')].filter(Boolean).join(' ');

  const globalAddress = [
    getValues('shipping_info_address_1'),
    getValues('shipping_info_address_2'),
    getValues('shipping_info_city'),
    getValues('shipping_info_state'),
    getValues('shipping_info_postal_code'),
    getValues('shipping_info_country_code')
  ]
    .filter(Boolean)
    .join(', ');

  const locationLabelId = receivingMethod === 'pick_up' ? 'pick_up_store' : 'address';

  return (
    <PageViewEvent eventName="Order_confirm_view">
      <div className="fixed inset-0 w-full h-full bg-black/85 flex justify-center items-center z-[110]">
        <div className="w-[600px] bg-white rounded-[20px] text-center flex flex-wrap justify-center p-10 pt-12">
          <div className="w-full text-[30px] leading-9 font-extrabold">
            <FormattedMessage id="confirm_your_details" defaultMessage="Confirm Your Details" />
          </div>
          <div className="w-full text-[20px] leading-6 font-medium mt-2 mb-[56px]">
            <FormattedMessage
              id="please_review_your_total_and_details"
              defaultMessage="Please review your total and details before continuing."
            />
          </div>
          <div className="w-full flex flex-col gap-4">
            <div className="w-full flex justify-between items-center text-[16px] leading-6 font-medium pb-4 border-b border-[#EDEDED]">
              <span className="text-[#737373]">
                <FormattedMessage id="order_section1_input1_label" defaultMessage="First name" />
              </span>
              <span>{getValues('customer_first_name')}</span>
            </div>
            <div className="w-full flex justify-between items-center text-[16px] leading-6 font-medium pb-4 border-b border-[#EDEDED]">
              <span className="text-[#737373]">
                <FormattedMessage id="order_section1_input2_label" defaultMessage="Last name" />
              </span>
              <span>{getValues('customer_last_name')}</span>
            </div>
            <div className="w-full flex justify-between items-center text-[16px] leading-6 font-medium pb-4 border-b border-[#EDEDED]">
              <span className="text-[#737373]">
                <FormattedMessage id="order_section1_input3_label" defaultMessage="Phone number" />
              </span>
              <span>{`+${getValues('customer_country_code_number')} ${getValues('customer_phone_number')}`}</span>
            </div>
            <div className="w-full flex justify-between items-center text-[16px] leading-6 font-medium pb-4 border-b border-[#EDEDED]">
              <span className="text-[#737373]">
                <FormattedMessage id="order_section1_input4_label" defaultMessage="E-mail" />
              </span>
              <span>{`${getValues('customer_email_1')}@${getValues('customer_email_2')}`}</span>
            </div>
            <div className="w-full flex justify-between items-center text-[16px] leading-6 font-medium pb-4 border-b border-[#EDEDED]">
              <span className="text-[#737373]">
                <FormattedMessage id={locationLabelId} defaultMessage={getLocationDefault(locationLabelId)} />
              </span>
              <span className="text-right">
                {receivingMethod === 'domestic'
                  ? domesticAddress
                  : receivingMethod === 'global'
                  ? globalAddress
                  : getValues('pickup_location')}
              </span>
            </div>
          </div>
          <div className="w-full flex justify-between items-center text-[18px] font-bold text-[#737373] mt-4 mb-[60px]">
            <FormattedMessage id="order_total" defaultMessage="Order Total" />
            <div className="text-[#262626]">{total}</div>
          </div>
          {!(receivingMethod === 'pick_up') && (
            <p className="w-full text-[14px] font-medium text-[#737373] pb-7">
              <FormattedMessage
                id={'check_modal_check_desc'}
                defaultMessage="Please ensure your information is correct, as refunds cannot be issued for delivery issues caused by incorrect details."
              />
            </p>
          )}
          <div className="w-full flex gap-4">
            <button
              className="!w-[132px] flex justify-center items-center h-[65px] rounded-[20px] bg-[#f5f5f5] text-[18px] font-bold data-[primary=true]:bg-[#ff6060] data-[primary=true]:text-white"
              onClick={() => onClose?.()}
            >
              Back
            </button>
            <button
              className="w-full flex justify-center items-center h-[65px] rounded-[20px] bg-[#f5f5f5] text-[18px] font-bold data-[primary=true]:bg-[#ff6060] data-[primary=true]:text-white disabled:opacity-60"
              data-primary
              onClick={() => {
                onContinueClick?.();
                trackEvent('Order_confirm_click_Continue_button');
              }}
              disabled={isSubmit}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </PageViewEvent>
  );
};

export default CheckModal;
